import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { withRouter } from "react-router";
import { Redirect, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import MPage from "../templates/mpage";
import FormInput from "../components/forminput";
import PasosVotacion from "../components/pasosvotacion";
import BotonAyuda from "../components/botonayuda";
import Cargando from "../components/cargando";
import ErrorMensaje from "../components/errormensaje";

import useObtenerMunicipio from "../../utils/useObtenerMunicipio";
import useTituloPagina from "../../utils/useTituloPagina";

const FormVotacion = (props) => {
  const [municipio, errorMunicipio] = useObtenerMunicipio(
    props.match.params.munUrl
  );
  useTituloPagina(municipio ? `Votar — ${municipio.nombre}` : null);
  const [propuestas, guardarPropuestas] = useState(undefined);
  const [error, guardarError] = useState(false);
  const [dniDisplay, setDniDisplay] = useState("");
  const [propuestaSeleccionada, setPropuestaSeleccionada] = useState(
    props.match.params.propuestaId || null
  );
  const [enviando, setEnviando] = useState(false);
  const [votoConfirmado, setVotoConfirmado] = useState(false);
  const [paso, setPaso] = useState(1);
  //Checkbox de aceptación de condiciones del último paso.
  const [acepta, setAcepta] = useState(false);

  //Estado de la verificación por SMS (inline, ya no en un popup).
  const [verificacionId, setVerificacionId] = useState(null);
  const [codigo, setCodigo] = useState(["", "", "", "", "", ""]);
  const [enviandoSMS, setEnviandoSMS] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [errorSMS, setErrorSMS] = useState(null);
  //Segundos que faltan para poder pedir otro código. Los manda el servidor: es
  //él quien decide la espera, y así el número no queda repetido acá.
  const [segundosEspera, setSegundosEspera] = useState(0);
  //Aviso (no error) cuando se pidió un código y todavía valía el anterior.
  const [avisoSMS, setAvisoSMS] = useState(null);
  //Última verificación exitosa: { tel, token }. Es estado (no ref) para que la
  //UI se actualice al verificar: muestra el mensaje de éxito y habilita el
  //botón "Siguiente".
  const [telVerificado, setTelVerificado] = useState(null);
  const otpRefs = useRef([]);
  //Barra de "Anterior / Siguiente": se usa para acercarla al elegir propuesta.
  const navegacionRef = useRef(null);

  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm();

  //Campo oculto de la propuesta elegida (se setea con las tarjetas).
  const propuestaField = register("propuestaId", {
    validate: (value) => !!value && value !== "0",
  });

  const usaSMS = municipio && municipio.verificacionSMS;
  //Números de paso: con SMS son 4 (el SMS es el 3 y las condiciones el 4); sin
  //SMS son 3 (las condiciones son el 3).
  const PASO_SMS = 3;
  const pasoCondiciones = usaSMS ? 4 : 3;

  useEffect(() => {
    if (municipio !== undefined) {
      const obtenerPropuestas = () => {
        axios
          .get(`/api/propuestas?municipioId=${municipio._id}&estado=aceptada`)
          .then((res) => {
            guardarPropuestas(res.data.propuestas);
          })
          .catch((error) => guardarError(true));
      };
      obtenerPropuestas();
    }
    return () => {
      guardarPropuestas(undefined);
    };
  }, [municipio]);

  //Refleja en el formulario la propuesta preseleccionada por la URL.
  useEffect(() => {
    if (propuestas !== undefined && propuestaSeleccionada) {
      if (propuestas.some((p) => p._id === propuestaSeleccionada)) {
        setValue("propuestaId", propuestaSeleccionada);
      } else {
        setPropuestaSeleccionada(null);
      }
    }
  }, [propuestas, propuestaSeleccionada, setValue]);

  const formatDNI = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6)
      return `${digits.slice(0, digits.length - 3)}.${digits.slice(-3)}`;
    return `${digits.slice(0, digits.length - 6)}.${digits.slice(digits.length - 6, digits.length - 3)}.${digits.slice(-3)}`;
  };

  const handleDniChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    setDniDisplay(formatDNI(e.target.value));
    setValue("dni", digits, { shouldValidate: true });
  };

  //Selección de propuesta desde las tarjetas (no avanza de paso: el vecino
  //confirma con "Siguiente").
  const actualizarPropuesta = (propuestaId) => {
    setValue("propuestaId", propuestaId, { shouldValidate: true });
    setPropuestaSeleccionada(propuestaId);
    clearErrors("propuestaId");

    //Se acerca "Siguiente" después de elegir. Con muchas propuestas el botón
    //queda muy abajo en el celular y no se ve que la selección haya quedado
    //hecha: el vecino toca "Seleccionar" y no pasa nada visible.
    //
    //Va en un setTimeout porque al marcar la tarjeta aparece el cartel
    //"✓ Seleccionada": la tarjeta crece y el botón se corre. Si se midiera
    //antes del re-render, el scroll quedaría unos píxeles arriba.
    setTimeout(() => {
      if (navegacionRef.current) {
        navegacionRef.current.scrollIntoView({
          behavior: comoDesplazarse(),
          block: "center",
        });
      }
    }, 0);
  };

  const validarFecha = (dia, mes, anio) => {
    if (!dia || !mes || !anio) return true;
    const d = parseInt(dia);
    const m = parseInt(mes) - 1;
    const a = parseInt(anio);
    const fecha = new Date(a, m, d);
    return (
      fecha.getFullYear() === a &&
      fecha.getMonth() === m &&
      fecha.getDate() === d
    );
  };

  //Deja el teléfono en el formato de 10 dígitos que exige el envío de SMS
  //(sin código de país, sin 0 y sin 15).
  const normalizarTel = (tel) => {
    let digitos = String(tel || "").replace(/\D/g, "");
    if (digitos.length > 10 && digitos.startsWith("549"))
      digitos = digitos.slice(3);
    else if (digitos.length > 10 && digitos.startsWith("54"))
      digitos = digitos.slice(2);
    if (digitos.startsWith("0")) digitos = digitos.slice(1);
    //Sacar el 15 de entre el código de área y el número (misma regla que
    //services/sms.js en el servidor: las dos copias tienen que coincidir).
    if (digitos.length === 12) {
      const areas = [2, 3, 4];
      for (let i = 0; i < areas.length; i++) {
        const n = areas[i];
        if (digitos.substr(n, 2) === "15") {
          digitos = digitos.slice(0, n) + digitos.slice(n + 2);
          break;
        }
      }
    }
    return digitos;
  };

  const noVacio = (val) =>
    val !== undefined && val !== null && String(val).trim() !== "";

  //Campos (con id) de cada paso, para limpiar sus errores al re-validar.
  const camposPaso = {
    1: ["propuestaId"],
    2: [
      "dni",
      "apellido",
      "nombre",
      "diaNac",
      "mesNac",
      "anioNac",
      "calle",
      "numero",
      "email",
      "tel",
    ],
  };

  //Valida el paso con los valores reales y devuelve el id del primer campo
  //inválido (o null). No se usa el booleano de react-hook-form porque en esta
  //versión valida el formulario completo, no el campo.
  const primerCampoInvalido = (p) => {
    const val = getValues();
    if (p === 1) {
      if (!val.propuestaId || val.propuestaId === "0") return "propuestaId";
    }
    if (p === 2) {
      const dniDigits = String(dniDisplay).replace(/\D/g, "");
      if (dniDigits.length < 7 || dniDigits.length > 8) return "dni";
      if (!noVacio(val.apellido)) return "apellido";
      if (!noVacio(val.nombre)) return "nombre";
      if (!noVacio(val.diaNac)) return "diaNac";
      if (!noVacio(val.mesNac)) return "mesNac";
      if (!noVacio(val.anioNac)) return "anioNac";
      if (!validarFecha(val.diaNac, val.mesNac, val.anioNac)) return "diaNac";
      if (!noVacio(val.calle)) return "calle";
      if (!noVacio(val.numero)) return "numero";
      if (!noVacio(val.email)) return "email";
      if (!noVacio(val.tel)) return "tel";
      if (usaSMS && normalizarTel(val.tel).length !== 10) return "tel";
    }
    return null;
  };

  const irArriba = () => window.scrollTo(0, 0);

  //El scroll suave hecho por JS no toma el "scroll-behavior: auto" que App.css
  //aplica con prefers-reduced-motion, así que la preferencia se consulta acá.
  const comoDesplazarse = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth";

  const enfocarCampo = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el.focus && el.type !== "hidden") el.focus({ preventScroll: true });
      }
    }, 0);
  };

  const siguiente = () => {
    clearErrors(camposPaso[paso] || []);
    const invalido = primerCampoInvalido(paso);
    if (invalido) {
      setError(invalido, { type: "manual" });
      if (invalido === "propuestaId") irArriba();
      else enfocarCampo(invalido);
      return;
    }
    //Al pasar de "Tus datos" a la verificación, se envía el código (salvo que
    //ese mismo teléfono ya haya sido verificado).
    if (usaSMS && paso === 2) {
      const telNorm = normalizarTel(getValues("tel"));
      setPaso(PASO_SMS);
      irArriba();
      if (!(telVerificado && telVerificado.tel === telNorm)) {
        enviarCodigo(telNorm);
      }
      return;
    }
    setPaso((p) => p + 1);
    irArriba();
  };

  const anterior = () => {
    setPaso((p) => Math.max(1, p - 1));
    irArriba();
  };

  //Cuenta regresiva del botón "Reenviar". Sin esto el botón queda habilitado
  //enseguida, el vecino lo toca, el servidor reutiliza el código anterior (no
  //manda otro SMS) y para él parece que no pasó nada: vuelve a tocar. Cada uno
  //de esos toques, pasada la espera, es un mensaje que se paga.
  useEffect(() => {
    if (segundosEspera <= 0) return undefined;
    const t = setTimeout(() => setSegundosEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [segundosEspera]);

  //--- Verificación por SMS (inline) ---------------------------------------
  const enviarCodigo = (telNorm) => {
    //Cada SMS se paga: no se dispara otro si hay uno en curso o si todavía
    //corre la espera. El botón ya está deshabilitado en esos casos, pero esto
    //cubre el doble clic rápido y cualquier otra vía que llame a esta función.
    if (enviandoSMS || segundosEspera > 0) return;
    setEnviandoSMS(true);
    setErrorSMS(null);
    setAvisoSMS(null);
    axios
      .post(`/api/verificaciones/${municipio._id}`, { tel: telNorm })
      .then((res) => {
        setVerificacionId(res.data.verificacionId);
        setEnviandoSMS(false);
        setSegundosEspera(res.data.esperaSegundos || 0);

        if (res.data.reutilizado) {
          //No salió un mensaje nuevo: el anterior sigue valiendo. Decirlo es
          //lo que evita que el vecino siga esperando un SMS que no viene.
          setAvisoSMS(
            "Ya te habíamos enviado un código hace unos segundos. Revisá tus " +
              "mensajes y escribí ese mismo código."
          );
          return;
        }

        //Solo se limpian las casillas cuando el código es realmente nuevo.
        setCodigo(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs.current[0] && otpRefs.current[0].focus(), 0);
      })
      .catch((err) => {
        setEnviandoSMS(false);
        setErrorSMS(
          err.response && err.response.data && err.response.data.errorMessage
            ? err.response.data.errorMessage
            : "No pudimos enviar el SMS. Intentá de nuevo en unos minutos."
        );
      });
  };

  const verificarCodigo = (codStr) => {
    const cod = codStr !== undefined ? codStr : codigo.join("");
    if (cod.length !== 6) {
      setErrorSMS("Ingresá los 6 dígitos del código.");
      return;
    }
    if (verificando) return;
    setVerificando(true);
    setErrorSMS(null);
    axios
      .post(`/api/verificaciones/confirmar/${verificacionId}`, { codigo: cod })
      .then((res) => {
        const telNorm = normalizarTel(getValues("tel"));
        //No se avanza solo: se marca el teléfono como verificado (muestra el
        //mensaje de éxito y habilita "Siguiente") y el vecino avanza cuando
        //quiera.
        setTelVerificado({ tel: telNorm, token: res.data.telVerificadoToken });
        setVerificando(false);
      })
      .catch((err) => {
        setVerificando(false);
        setCodigo(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs.current[0] && otpRefs.current[0].focus(), 0);
        setErrorSMS(
          err.response && err.response.data && err.response.data.errorMessage
            ? err.response.data.errorMessage
            : "Código incorrecto."
        );
      });
  };

  const handleOtpChange = (i, value) => {
    const d = value.replace(/\D/g, "").slice(-1);
    const nuevo = [...codigo];
    nuevo[i] = d;
    setCodigo(nuevo);
    if (d && i < 5) otpRefs.current[i + 1] && otpRefs.current[i + 1].focus();
    //Con las 6 casillas completas, verifica solo.
    if (nuevo.every((x) => x !== "")) verificarCodigo(nuevo.join(""));
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && !codigo[i] && i > 0) {
      otpRefs.current[i - 1] && otpRefs.current[i - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digitos = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    const nuevo = ["", "", "", "", "", ""];
    digitos.split("").forEach((d, j) => {
      nuevo[j] = d;
    });
    setCodigo(nuevo);
    if (digitos.length === 6) verificarCodigo(nuevo.join(""));
    else {
      const next = Math.min(digitos.length, 5);
      otpRefs.current[next] && otpRefs.current[next].focus();
    }
  };

  //--- Envío final del voto -------------------------------------------------
  const enviarVoto = () => {
    const val = getValues();
    const localidad = municipio.nombre.slice(0, -4).trim();
    const data = {
      municipioId: municipio._id,
      propuestaId: val.propuestaId,
      propuestaTitulo: (
        propuestas.find((p) => p._id === val.propuestaId) || {}
      ).titulo,
      dni: String(dniDisplay).replace(/\D/g, ""),
      nombre: `${String(val.apellido).toUpperCase()} ${String(val.nombre).toUpperCase()}`,
      fechaNac: `${val.anioNac}-${String(val.mesNac).padStart(2, "0")}-${String(val.diaNac).padStart(2, "0")}`,
      dir: `${val.calle} ${val.numero}, ${localidad}`.toUpperCase(),
      email: val.email,
      tel: usaSMS ? normalizarTel(val.tel) : val.tel,
    };
    if (usaSMS) {
      data.telVerificadoToken = telVerificado ? telVerificado.token : null;
    }

    setEnviando(true);
    axios
      .post(`/api/votos/${municipio._id}`, data)
      .then(() => {
        setEnviando(false);
        setVotoConfirmado(true);
        irArriba();
      })
      .catch((err) => {
        setEnviando(false);
        Swal.fire(
          err.response && err.response.data && err.response.data.errorMessage
            ? err.response.data.errorMessage
            : "No pudimos registrar tu voto.",
          `Si el problema sigue, comunicate con el municipio: ${municipio.email} — ${municipio.tel}.`,
          "error"
        );
        //Si venció la verificación del teléfono, se vuelve al paso del SMS.
        if (usaSMS) {
          setTelVerificado(null);
          setAcepta(false);
          setPaso(PASO_SMS);
        }
      });
  };

  if (errorMunicipio === true || error === true)
    return (
      <ErrorMensaje
        titulo="No pudimos cargar la votación"
        mensaje="Puede que el enlace sea incorrecto o que haya un problema momentáneo de conexión."
      />
    );
  if (municipio === undefined || propuestas === undefined) return <Cargando />;
  if (municipio.etapa !== "votacion" || municipio.tipoVotacion === "privada")
    return <Redirect to={`/municipio/${municipio.nombreUrl}`} />;

  //Pantalla de confirmación tras registrar el voto.
  if (votoConfirmado) {
    return (
      <MPage municipio={municipio}>
        <div className="text-center py-4">
          <div
            className="text-success"
            style={{ fontSize: "3.5rem", lineHeight: 1 }}
            aria-hidden="true"
          >
            ✓
          </div>
          <h1 className="h3 ppr-color-azul font-weight-bold mt-2">
            ¡Tu voto fue registrado!
          </h1>
          <p className="mt-2">
            Gracias por participar del Presupuesto Participativo de{" "}
            {municipio.nombre}.
          </p>
          <div
            className="card mx-auto mt-3 text-left"
            style={{ maxWidth: "34rem" }}
          >
            <div className="card-header">Qué sigue</div>
            <div className="card-body">
              <p>
                Vamos a <b>fiscalizar</b> la votación para garantizar que todo
                sea transparente.
              </p>
              <p className="mb-0">
                Después vamos a <b>publicar los resultados</b> en la página
                principal del municipio.
              </p>
            </div>
          </div>
          <Link
            to={`/municipio/${municipio.nombreUrl}`}
            className="btn ppr-btn-verde font-weight-bold mt-4"
          >
            Volver al inicio
          </Link>
        </div>
      </MPage>
    );
  }

  const nombresPasos = usaSMS
    ? [
        "Elegí una propuesta",
        "Tus datos",
        "Verificá tu celular",
        "Confirmá tu voto",
      ]
    : ["Elegí una propuesta", "Tus datos", "Confirmá tu voto"];

  const telNormActual = normalizarTel(getValues("tel"));
  const yaVerificado =
    telVerificado && telVerificado.tel === telNormActual;

  return (
    <MPage municipio={municipio}>
      <PasosVotacion
        pasos={nombresPasos}
        pasoActual={paso}
        etiqueta="Progreso de la votación"
      />
      <BotonAyuda municipio={municipio} />

      <div className="row">
        <div className="col-12">
          <h1 className="ppr-titulo centrado my-2">
            Formulario de Votación de Propuesta
          </h1>
          <p className="mb-1">
            Recordá que solo se permite votar una vez.{" "}
            <Link
              to={"/municipio/" + municipio.nombreUrl + "/info"}
              className="ppr-a"
            >
              Más información sobre quiénes pueden votar.
            </Link>
          </p>
          <small className="text-muted">
            Los campos indicados con <b>*</b> son obligatorios.
          </small>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {/*Sin submit nativo: el voto se envía solo con el botón del último
             paso, para que no se dispare antes de tiempo.*/}
          <form
            onSubmit={(e) => e.preventDefault()}
            style={{ paddingBottom: "80px" }}
          >
            {/* ------------------ PASO 1: ELEGIR PROPUESTA ------------------ */}
            {paso === 1 && (
              <fieldset>
                <h2 className="ppr-titulo mt-3 h4">Elegí una propuesta</h2>
                <p className="mb-2">
                  Tocá <b>“Seleccionar”</b> en la propuesta que querés votar.
                </p>
                <input type="hidden" id="propuestaId" {...propuestaField} />
                {errors.propuestaId && (
                  <small className="text-danger form-text mb-2 d-block">
                    Elegí una propuesta para poder votar.
                  </small>
                )}

                <div className="row">
                  {propuestas.map((propuesta) => {
                    const seleccionada =
                      propuestaSeleccionada === propuesta._id;
                    return (
                      <div
                        className="col-12 col-md-6 mb-3"
                        key={propuesta._id}
                      >
                        <div
                          className={`card h-100 ${seleccionada ? "border-success" : ""}`}
                          style={seleccionada ? { borderWidth: "2px" } : {}}
                        >
                          <div className="row no-gutters">
                            <div className="col-4">
                              <img
                                className="img-fluid h-100"
                                style={{ objectFit: "cover" }}
                                src={`/api/public/${propuesta.img}`}
                                alt={propuesta.titulo}
                                loading="lazy"
                              />
                            </div>
                            <div className="col-8">
                              <div className="card-body p-2">
                                {seleccionada && (
                                  <span className="badge badge-success mb-1 d-block">
                                    ✓ Seleccionada
                                  </span>
                                )}
                                {propuesta.eje && (
                                  <span className="badge badge-primary tag">
                                    {propuesta.eje}
                                  </span>
                                )}
                                <h6 className="card-title text-center m-1">
                                  {propuesta.titulo}
                                </h6>
                                <small className="card-text text-center d-block m-1">
                                  {propuesta.subtitulo}
                                </small>
                                <div className="text-center mt-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      actualizarPropuesta(propuesta._id)
                                    }
                                    className={`btn btn-sm font-weight-bold ${
                                      seleccionada ? "btn-success" : "ppr-btn-azul"
                                    }`}
                                  >
                                    {seleccionada
                                      ? "✓ Seleccionada"
                                      : "Seleccionar"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            )}

            {/* -------------------- PASO 2: TUS DATOS -------------------- */}
            {paso === 2 && (
              <fieldset>
                <h2 className="ppr-titulo mt-3 h4">Datos personales</h2>

                <div className="form-group">
                  <label htmlFor="dni">* DNI:</label>
                  <input
                    {...register("dni", {
                      required: true,
                      validate: (value) => {
                        const digits = String(value || "").replace(/\D/g, "");
                        return digits.length >= 7 && digits.length <= 8;
                      },
                    })}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`form-control ${errors.dni ? "is-invalid" : ""}`}
                    id="dni"
                    placeholder="Ej: 12.345.678"
                    value={dniDisplay}
                    onChange={handleDniChange}
                  />
                  {errors.dni && (
                    <small className="text-danger form-text">
                      Ingresá un DNI válido (7 u 8 dígitos).
                    </small>
                  )}
                </div>

                <div className="row">
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register}
                      error={errors.nombre}
                      label={"* Nombres:"}
                      id={"nombre"}
                      isRequired={true}
                      placeholder="Como figuran en el DNI."
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register}
                      error={errors.apellido}
                      label={"* Apellidos:"}
                      id={"apellido"}
                      isRequired={true}
                      placeholder="Como figuran en el DNI."
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>* Fecha de Nacimiento:</label>
                  <div className="row">
                    <div className="col-3 col-md-2">
                      <input
                        {...register("diaNac", {
                          required: true,
                          min: 1,
                          max: 31,
                          validate: (value) => {
                            const { mesNac, anioNac } = getValues();
                            return (
                              validarFecha(value, mesNac, anioNac) ||
                              "Fecha inválida"
                            );
                          },
                        })}
                        type="number"
                        inputMode="numeric"
                        autoComplete="bday-day"
                        placeholder="Día"
                        min="1"
                        max="31"
                        id="diaNac"
                        className={`form-control ${errors.diaNac ? "is-invalid" : ""}`}
                      />
                      {errors.diaNac && (
                        <small className="text-danger form-text">
                          {errors.diaNac.message || "Día inválido."}
                        </small>
                      )}
                    </div>

                    <div className="col-5 col-md-4">
                      <select
                        {...register("mesNac", { required: true })}
                        autoComplete="bday-month"
                        id="mesNac"
                        className={`form-control ${errors.mesNac ? "is-invalid" : ""}`}
                      >
                        <option value="">Mes</option>
                        <option value="01">Enero</option>
                        <option value="02">Febrero</option>
                        <option value="03">Marzo</option>
                        <option value="04">Abril</option>
                        <option value="05">Mayo</option>
                        <option value="06">Junio</option>
                        <option value="07">Julio</option>
                        <option value="08">Agosto</option>
                        <option value="09">Septiembre</option>
                        <option value="10">Octubre</option>
                        <option value="11">Noviembre</option>
                        <option value="12">Diciembre</option>
                      </select>
                      {errors.mesNac && (
                        <small className="text-danger form-text">
                          Seleccioná un mes.
                        </small>
                      )}
                    </div>

                    <div className="col-4 col-md-3">
                      <input
                        {...register("anioNac", {
                          required: true,
                          min: 1900,
                          valueAsNumber: true,
                        })}
                        type="number"
                        inputMode="numeric"
                        autoComplete="bday-year"
                        placeholder="Año"
                        min="1900"
                        max={new Date().getFullYear()}
                        id="anioNac"
                        className={`form-control ${errors.anioNac ? "is-invalid" : ""}`}
                      />
                      {errors.anioNac && (
                        <small className="text-danger form-text">
                          {`Año inválido (1900-${new Date().getFullYear()}).`}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>* Domicilio:</label>
                  <div className="row">
                    <div className="col-8 col-md-5">
                      <input
                        {...register("calle", { required: true })}
                        type="text"
                        autoComplete="address-line1"
                        id="calle"
                        className={`form-control ${errors.calle ? "is-invalid" : ""}`}
                        placeholder="Ej: Av. San Martín"
                      />
                      {errors.calle && (
                        <small className="text-danger form-text">
                          Campo Obligatorio.
                        </small>
                      )}
                    </div>
                    <div className="col-4 col-md-2">
                      <input
                        {...register("numero", { required: true })}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        id="numero"
                        className={`form-control ${errors.numero ? "is-invalid" : ""}`}
                        placeholder="Ej: 123"
                      />
                      {errors.numero && (
                        <small className="text-danger form-text">
                          Obligatorio.
                        </small>
                      )}
                    </div>
                    <div className="col-12 col-md-5 mt-2 mt-md-0">
                      <input
                        type="text"
                        className="form-control"
                        value={municipio.nombre.slice(0, -4).trim()}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <FormInput
                  register={register}
                  error={errors.email}
                  label={"* Correo Electrónico:"}
                  id={"email"}
                  isRequired={true}
                  placeholder="Ej: tunombre@correo.com"
                  type="email"
                  autoComplete="email"
                />
                <FormInput
                  register={register}
                  error={errors.tel}
                  label={
                    usaSMS
                      ? "* Teléfono celular (te enviaremos un código por SMS):"
                      : "* Teléfono:"
                  }
                  id={"tel"}
                  isRequired={true}
                  ayuda={
                    usaSMS
                      ? "Cód. de área + número, sin 0 y sin 15. Ej: 3511234567"
                      : undefined
                  }
                  placeholder={
                    usaSMS ? "Ej: 3511234567" : "Ej: 11 2345-6789"
                  }
                  type="tel"
                  autoComplete="tel"
                />
              </fieldset>
            )}

            {/* --------------- PASO 3: VERIFICAR CELULAR (SMS) --------------- */}
            {usaSMS && paso === PASO_SMS && (
              <fieldset>
                <h2 className="ppr-titulo mt-3 h4">Verificá tu celular</h2>
                <div className="card mx-auto mt-3" style={{ maxWidth: "34rem" }}>
                  <div className="card-body p-4 text-center">
                    {yaVerificado ? (
                      <p
                        className="text-success font-weight-bold mb-0 py-3"
                        style={{ fontSize: "1.1rem" }}
                      >
                        ✓ ¡Teléfono verificado con éxito!
                        <br />
                        Tocá “Siguiente” para continuar.
                      </p>
                    ) : (
                      <>
                        <p className="mb-4">
                          Te enviamos un código por SMS al{" "}
                          <b>{telNormActual}</b>.
                          <br />
                          Ingresá los 6 dígitos:
                        </p>
                        <div
                          className="otp-inputs my-4"
                          onPaste={handleOtpPaste}
                        >
                          {codigo.map((d, i) => (
                            <input
                              key={i}
                              ref={(el) => (otpRefs.current[i] = el)}
                              className="otp-input"
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={d}
                              autoComplete={i === 0 ? "one-time-code" : "off"}
                              aria-label={`Dígito ${i + 1}`}
                              onChange={(e) =>
                                handleOtpChange(i, e.target.value)
                              }
                              onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            />
                          ))}
                        </div>
                        {enviandoSMS && (
                          <p className="text-muted mt-3 mb-0">
                            Enviando código...
                          </p>
                        )}
                        {avisoSMS && (
                          <p className="text-muted mt-3 mb-0" role="status">
                            {avisoSMS}
                          </p>
                        )}
                        {errorSMS && (
                          <p className="text-danger mt-3 mb-0" role="alert">
                            {errorSMS}
                          </p>
                        )}
                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn ppr-btn-verde font-weight-bold m-2"
                            onClick={() => verificarCodigo()}
                            disabled={verificando || enviandoSMS}
                          >
                            {verificando ? "Verificando..." : "Verificar código"}
                          </button>
                          {/* La cuenta regresiva va DENTRO del botón y no en un
                              aviso aparte: un texto que cambia cada segundo con
                              role="status" se lo lee entero el lector de
                              pantalla, una vez por segundo. */}
                          <button
                            type="button"
                            className="btn ppr-btn-azul font-weight-bold m-2"
                            onClick={() => enviarCodigo(telNormActual)}
                            disabled={enviandoSMS || segundosEspera > 0}
                          >
                            {segundosEspera > 0
                              ? `Reenviar código (${segundosEspera} s)`
                              : "Reenviar código"}
                          </button>
                        </div>
                        {segundosEspera > 0 && (
                          <p className="text-muted small mt-2 mb-0">
                            El mensaje puede tardar un momento en llegar. Si no
                            te llega, vas a poder pedir otro cuando termine la
                            cuenta.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </fieldset>
            )}

            {/* ----------------- PASO FINAL: CONFIRMAR VOTO ----------------- */}
            {paso === pasoCondiciones && (
              <fieldset>
                <h2 className="ppr-titulo mt-3 h4">Confirmá tu voto</h2>
                <div className="card mx-auto mt-3" style={{ maxWidth: "40rem" }}>
                  <div className="card-body p-4">
                    <p className="h6 font-weight-bold ppr-color-azul mb-2">
                      Esta es una declaración jurada
                    </p>
                    <p className="mb-3">
                      Al enviar tu voto, declarás bajo juramento que:
                    </p>
                    <ul className="mb-3" style={{ lineHeight: 1.8 }}>
                      <li className="mb-2">
                        Los datos personales que ingresaste son verídicos,
                        completos y correctos.
                      </li>
                      <li className="mb-2">
                        Estás habilitado/a para participar según las bases y
                        condiciones vigentes.
                      </li>
                      <li className="mb-2">
                        Cumplís con los requisitos de edad y lugar de residencia
                        exigidos.
                      </li>
                    </ul>
                    {/*La responsabilidad se saca de la lista y se destaca en un
                       recuadro de aviso, por su peso legal.*/}
                    <div className="alert alert-warning mb-4">
                      <b>Asumís la responsabilidad</b> por cualquier inexactitud
                      u omisión en los datos que declarás.
                    </div>
                    <div
                      className="p-3"
                      style={{
                        backgroundColor: "var(--celeste-mas-claro)",
                        borderRadius: "var(--radio-control)",
                      }}
                    >
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="acepta"
                          checked={acepta}
                          onChange={(e) => setAcepta(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="acepta">
                          Acepto las condiciones y confirmo que mis datos son
                          correctos.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            )}

            {/* --------------------- NAVEGACIÓN DE PASOS --------------------- */}
            <div
              ref={navegacionRef}
              className="d-flex justify-content-between align-items-center mt-4 mb-2"
            >
              {paso > 1 ? (
                <button
                  type="button"
                  onClick={anterior}
                  className="btn ppr-btn-azul font-weight-bold"
                >
                  ← Anterior
                </button>
              ) : (
                <Link
                  to={`/municipio/${municipio.nombreUrl}`}
                  className="btn ppr-btn-azul font-weight-bold"
                >
                  Volver
                </Link>
              )}

              {(paso === 1 || paso === 2) && (
                <button
                  type="button"
                  onClick={siguiente}
                  className="btn ppr-btn-verde font-weight-bold"
                >
                  Siguiente →
                </button>
              )}

              {usaSMS && paso === PASO_SMS && (
                //Siempre visible, pero deshabilitado hasta pasar la verificación.
                <button
                  type="button"
                  onClick={() => {
                    setPaso(pasoCondiciones);
                    irArriba();
                  }}
                  className="btn ppr-btn-verde font-weight-bold"
                  disabled={!yaVerificado}
                >
                  Siguiente →
                </button>
              )}

              {paso === pasoCondiciones && (
                <button
                  type="button"
                  onClick={enviarVoto}
                  className="btn ppr-btn-verde font-weight-bold"
                  disabled={!acepta || enviando}
                >
                  {enviando ? "Enviando..." : "Enviar voto"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </MPage>
  );
};

export default withRouter(FormVotacion);
